import { ExpandableTable } from "../../../../components/UniversalTable";
import { documentTypeTableColumns } from "../../../../helpers";
import { UploadedIncomeSource } from "../../../../types/income";
import { PaystubItem } from "../../../../types/verified/paystub";
import { useMemo } from "react";
import { DocumentVerificationProvider } from "shared/types";
import { ExpandedTableRow } from "./slices/ExpandedTableRow";
import { collectFailedPaystubRows, createInscribePaystubRows } from "./utils";
import { resolveDocumentUrl } from "../../../../utils/fraudRisk";

const sortPaystubItems = (a: PaystubItem, b: PaystubItem) => {
  const dateA = a.income_source_data?.additional_data?.pay_period_start_date;
  const dateB = b.income_source_data?.additional_data?.pay_period_start_date;

  if (!dateA) return 1;
  if (!dateB) return -1;

  return new Date(dateB).getTime() - new Date(dateA).getTime();
};

export const PaystubTable = ({
  incomeItems = [],
  provider,
  failedIncomeSources = []
}: {
  incomeItems?: PaystubItem[];
  provider: DocumentVerificationProvider;
  failedIncomeSources?: UploadedIncomeSource[];
}) => {
  const rows = useMemo(() => {
    if (provider === DocumentVerificationProvider.Inscribe) {
      const sortedIncomeItems = [...incomeItems].sort(sortPaystubItems);
      const inscribeRows = createInscribePaystubRows(sortedIncomeItems);
      const itemSourceIds = inscribeRows.reduce<Set<number>>((acc, r) => {
        if (r.income_source_id !== undefined) acc.add(r.income_source_id);
        return acc;
      }, new Set());
      const failedRows = collectFailedPaystubRows(
        failedIncomeSources,
        itemSourceIds
      );
      return [...inscribeRows, ...failedRows];
    }

    const flatPaystubs = incomeItems.flatMap(
      (i) =>
        i.data?.document?.income_sources?.flatMap(
          (incSource) => incSource.pay_stubs
        ) ?? []
    );
    const flatWarnings = incomeItems.flatMap((i) =>
      (i.data?.document?.warnings || []).filter(
        (warning) => (warning as { type?: string }).type !== "name_mismatch"
      )
    );
    const failedRows = collectFailedPaystubRows(failedIncomeSources);
    return [...flatWarnings, ...flatPaystubs, ...failedRows].filter(Boolean);
  }, [incomeItems, provider, failedIncomeSources]);

  return (
    <ExpandableTable
      cellClassName="p-4 pl-0 text-sm self-center"
      headerRowClassName="text-content-300 font-medium"
      renderExpanded={(row) => (
        <div className="p-6 bg-background-900">
          <ExpandedTableRow
            rowData={row as Parameters<typeof ExpandedTableRow>[0]["rowData"]}
            provider={provider}
          />
        </div>
      )}
      rows={rows}
      columns={documentTypeTableColumns}
      getViewUrl={resolveDocumentUrl}
    />
  );
};
